import React, { useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import { Button, Input, SalvModal } from '@salvapp/ui-components';
import { Form, Formik, FormikHelpers, FormikProps } from 'formik';
import { FormikState } from 'formik/dist/types';
import * as Yup from 'yup';

import './ScreeningAlertFiltersModal.scss';
import ScreeningAlertFilterCondition, { ScreeningAlertFiltersCondition } from './ScreeningAlertFilterCondition';
import { ScreeningAlertFilterObjects, toDropdownOption } from './helper';
import { DropdownOption } from '@salvapp/ui-components/build/dropdown/dropdown.types';
import dayjs from 'dayjs';
import { useDispatch } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/pro-regular-svg-icons/faPlus';
import { showFeedback } from '../../../feedback/feedbackReducer';
import {
  ApiScreeningAlertFilter,
  ApiScreeningAlertFilterExpressionFieldTypeEnum,
  ApiScreeningAlertFilterExpressionOperationEnum,
  ApiScreeningAlertFilterExpressionTargetEnum,
  screeningAlertFilterApi,
} from '../../../apis';
import { showConfirmationModal } from '../../../confirmation-modal/confirmationModalReducer';
import { parseErrorMessage } from '../../../common/errors/helpers';

const validationSchema = Yup.object().shape({
  name: Yup.string().required('Required'),
  expressions: Yup.array()
    .of(
      Yup.object({
        value: Yup.mixed().required(),
      })
    )
    .required('Required'),
});

export interface ScreeningAlertFiltersModalFormValues {
  id?: number;
  name: string;
  expressions: ScreeningAlertFiltersCondition[];
}

export const ScreeningAlertFiltersModal = ({
  selectedFilter,
  screeningAlertFiltersObjects,
  onSubmit,
}: Props): React.JSX.Element | null => {
  const dispatch = useDispatch();
  const [showModal, setShowModal] = useState(false);
  const initialValues: {
    id?: number;
    name: string;
    expressions: ScreeningAlertFiltersCondition[];
  } = {
    id: selectedFilter?.id,
    name: selectedFilter?.name || '',
    expressions: [],
  };

  const initialFormikState: Partial<FormikState<ScreeningAlertFiltersModalFormValues>> = {
    values: initialValues,
    isSubmitting: false,
    isValidating: false,
  };

  const deleteFilter = async (id: number | undefined, setSubmitting: any, resetForm: any) => {
    try {
      if (id) {
        dispatch(
          showConfirmationModal({
            title: 'Are you sure?',
            confirmationText: 'Yes, delete it!',
            cancelText: 'No, keep it',
            onConfirm: async () => {
              setSubmitting(true);
              await screeningAlertFilterApi.deleteFilter({ id });
              closeModal(resetForm);
              setSubmitting(false);
              onSubmit();
            },
          })
        );
      }
    } catch (e) {
      console.error(`Deleting filter ${id} failed`, e);
      dispatch(
        showFeedback({
          message: `Deleting filter ${id} failed`,
          success: false,
        })
      );
    }
  };

  const submit = async (
    values: ScreeningAlertFiltersModalFormValues,
    { setStatus, setSubmitting, resetForm }: FormikHelpers<ScreeningAlertFiltersModalFormValues>
  ) => {
    setSubmitting(true);
    try {
      const expressions = values.expressions.flatMap((expression) => {
        const formattedExpression = {
          ...expression,
          fieldType: expression.fieldType as ApiScreeningAlertFilterExpressionFieldTypeEnum,
          operation: expression.operation.key as ApiScreeningAlertFilterExpressionOperationEnum,
          target: expression.target.key as ApiScreeningAlertFilterExpressionTargetEnum,
          field: expression.field.key,
        };

        // For the STRINGLIST type format values
        if (expression.fieldType === ApiScreeningAlertFilterExpressionFieldTypeEnum.STRING_LIST) {
          return {
            ...formattedExpression,
            value: JSON.stringify(expression.value.map((value: DropdownOption) => value.key)),
          };
        }

        // For the TIMESTAMP type format it
        if (expression.fieldType === ApiScreeningAlertFilterExpressionFieldTypeEnum.TIMESTAMP) {
          if (expression.operation.key === 'IS_BETWEEN') {
            // At the moment "in between" is saved as two separate expressions
            return [
              {
                ...formattedExpression,
                operation: ApiScreeningAlertFilterExpressionOperationEnum.GREATER_THAN,
                value: expression.value[0].valueOf(),
              },
              {
                ...formattedExpression,
                operation: ApiScreeningAlertFilterExpressionOperationEnum.LESS_THAN,
                value: expression.value[1].valueOf(),
              },
            ];
          }
          return {
            ...formattedExpression,
            value: expression.value.valueOf(), // This returns date in UNIX ms
          };
        }

        // For the NUMBER type format it in case of IS_BETWEEN
        if (expression.fieldType === ApiScreeningAlertFilterExpressionFieldTypeEnum.NUMBER) {
          if (expression.operation.key === 'IS_BETWEEN') {
            // At the moment "in between" is saved as two separate expressions
            return [
              {
                ...formattedExpression,
                operation: ApiScreeningAlertFilterExpressionOperationEnum.GREATER_THAN,
                value: Number(expression.value[0]),
              },
              {
                ...formattedExpression,
                operation: ApiScreeningAlertFilterExpressionOperationEnum.LESS_THAN,
                value: Number(expression.value[1]),
              },
            ];
          }
          return {
            ...formattedExpression,
            value: Number(expression.value), // This returns number
          };
        }
        return formattedExpression;
      });

      const filter = values.id
        ? await screeningAlertFilterApi.updateFilter({
            id: values.id,
            apiNewScreeningAlertFilter: {
              name: values.name,
              expressions,
            },
          })
        : await screeningAlertFilterApi.addFilter({
            apiNewScreeningAlertFilter: {
              name: values.name,
              expressions,
            },
          });

      setShowModal(false);
      resetForm(initialFormikState);
      onSubmit(filter);
    } catch (e) {
      setStatus(await parseErrorMessage(e));
    } finally {
      setSubmitting(false);
    }
  };

  const closeModal = (resetForm: any) => {
    setShowModal(false);
    resetForm({
      isSubmitting: false,
      isValidating: false,
      values: initialValues,
    });
  };

  const removeCondition = (expressions: any, index: number, setFieldValue: any) => {
    expressions.splice(index, 1);
    setFieldValue('expressions', expressions);
  };

  const updateCondition = (
    expressions: ScreeningAlertFiltersCondition[],
    index: number,
    setFieldValue: any,
    newValue: ScreeningAlertFiltersCondition
  ) => {
    expressions[index] = newValue;
    setFieldValue('expressions', expressions);
  };

  const setFilter = (setFieldValue: any) => {
    setShowModal(true);
    setFieldValue('name', selectedFilter?.name);
    setFieldValue('id', selectedFilter?.id);
    // Format back from database entry to objects used in FE
    const formattedExpressions = selectedFilter?.expressions.map((expression) => {
      const target = screeningAlertFiltersObjects.objects.find((object) => object.value.key === expression.target);
      const field = target?.fields.find((field) => field.value.key === expression.field);
      const operation = field?.operations.find((operation) => operation.key === expression.operation);
      let value: any = expression.value;
      if (expression.fieldType === ApiScreeningAlertFilterExpressionFieldTypeEnum.STRING_LIST) {
        const values = JSON.parse(expression.value);
        value = values.map(
          (value: any) => field?.options?.find((option) => option.key === value) || toDropdownOption(value)
        );
      } else if (expression.fieldType === ApiScreeningAlertFilterExpressionFieldTypeEnum.TIMESTAMP) {
        value = dayjs(Number(expression.value));
      }
      return {
        ...expression,
        target: target?.value,
        field: field?.value,
        operation,
        value,
      };
    });
    setFieldValue('expressions', formattedExpressions);
  };

  return (
    <Formik initialValues={initialValues} onSubmit={submit} validationSchema={validationSchema} enableReinitialize>
      {(formikProps: FormikProps<ScreeningAlertFiltersModalFormValues>) => {
        const { isSubmitting, values, setFieldValue, setFieldTouched, resetForm, touched, errors } = formikProps;
        return (
          <>
            <div className='buttons'>
              {selectedFilter && (
                <Button
                  variant='white'
                  size='large'
                  onClick={() => {
                    setFilter(setFieldValue);
                  }}
                >
                  Edit filter
                </Button>
              )}
              <Button
                variant='white'
                size='large'
                onClick={() => {
                  setShowModal(true);
                  setFieldValue('id', undefined);
                  setFieldValue('name', '');

                  setFieldValue('expressions', [screeningAlertFiltersObjects.createNewDefaultValues()]);
                }}
              >
                <FontAwesomeIcon className='fe me-2' icon={faPlus} />
                New filter
              </Button>
            </div>
            <SalvModal show={showModal} onHide={() => closeModal(resetForm)} className='screening-alert-filters-modal'>
              <Form>
                <SalvModal.Header>Filter</SalvModal.Header>
                <SalvModal.Body>
                  <Row>
                    <Col md={12}>
                      <Input
                        id='name'
                        name='name'
                        label='Name'
                        value={values.name}
                        inputSize='large'
                        onChange={async (event: any) => {
                          await setFieldValue('name', event.target.value);
                          await setFieldTouched('name');
                        }}
                        isInvalid={touched.name && !values.name}
                      />
                    </Col>
                  </Row>
                  <Row key={values.expressions.length} className='mt-4'>
                    <Col md={12}>
                      <label>Filter conditions</label>
                      {values.expressions.map((expression, index) => (
                        <ScreeningAlertFilterCondition
                          key={index}
                          expression={expression}
                          removable={index !== 0}
                          screeningAlertFiltersObjects={screeningAlertFiltersObjects}
                          onConditionChange={(change: any) =>
                            updateCondition(values.expressions, index, setFieldValue, change)
                          }
                          removeCondition={() => removeCondition(values.expressions, index, setFieldValue)}
                        />
                      ))}
                      <div className='mt-3'>
                        <Button
                          variant='secondary'
                          onClick={() => {
                            setFieldValue('expressions', [
                              ...values.expressions,
                              screeningAlertFiltersObjects.createNewDefaultValues(),
                            ]);
                          }}
                        >
                          <FontAwesomeIcon className='fe me-2' icon={faPlus} aria-label='Add condition' />
                          Add condition
                        </Button>
                      </div>
                    </Col>
                  </Row>
                </SalvModal.Body>
                <SalvModal.Footer>
                  {values.id && (
                    <Button
                      disabled={isSubmitting}
                      onClick={() => deleteFilter(values.id, formikProps.setSubmitting, resetForm)}
                      size='large'
                      variant='outline-danger'
                    >
                      Delete
                    </Button>
                  )}
                  <Button variant='secondary' size='large' onClick={() => closeModal(resetForm)}>
                    Close
                  </Button>
                  {values.id && (
                    <Button
                      disabled={
                        isSubmitting ||
                        Object.keys(errors).length > 0 ||
                        !values.expressions.every(
                          (expression) =>
                            expression.value?.length || (!Array.isArray(expression.value) && expression.value)
                        )
                      }
                      onClick={() => {
                        if (values.name) {
                          submit({ ...values, id: undefined }, formikProps);
                        }
                      }}
                      size='large'
                      variant='outline-primary'
                    >
                      Save as new
                    </Button>
                  )}
                  <Button
                    disabled={
                      isSubmitting ||
                      Object.keys(errors).length > 0 ||
                      !values.expressions.every(
                        (expression) =>
                          expression.value?.length || (!Array.isArray(expression.value) && expression.value)
                      )
                    }
                    type='submit'
                    size='large'
                    variant='primary'
                  >
                    Save
                  </Button>
                </SalvModal.Footer>
              </Form>
            </SalvModal>
          </>
        );
      }}
    </Formik>
  );
};

interface Props {
  selectedFilter: ApiScreeningAlertFilter | undefined;
  onSubmit: any;
  screeningAlertFiltersObjects: ScreeningAlertFilterObjects;
}

export default ScreeningAlertFiltersModal;
